/**
 * Keeps asking the phone where it is, and keeps the last answer.
 *
 * It watches instead of asking once per point, because the first reading after
 * a cold start is the worst one the phone will give — the chip needs half a
 * minute to see enough satellites. Watching from the moment the app opens means
 * that by the time there is something to pin, the reading is already good.
 *
 * The cost is battery, and it is the right trade: the phone is in a hand, not
 * in a pocket for a week.
 */
import type { Sample } from "./position.js";

/** What can go wrong, kept as codes so the wording lives with the screen. */
export type GeoProblem = "unsupported" | "denied" | "unavailable" | "timeout";

export interface Watcher {
  stop(): void;
}

/** Gives up on a reading after this long and says so, rather than hanging. */
const TIMEOUT_MS = 30_000;

const OPTIONS: PositionOptions = {
  // Without this the phone answers from the mobile network, which is accurate
  // to a few hundred metres and completely useless here.
  enableHighAccuracy: true,
  // Never accept a cached reading: knowing how old it is, is half the data.
  maximumAge: 0,
  timeout: TIMEOUT_MS,
};

function problemOf(error: GeolocationPositionError): GeoProblem {
  if (error.code === error.PERMISSION_DENIED) return "denied";
  if (error.code === error.TIMEOUT) return "timeout";
  return "unavailable";
}

/**
 * Starts watching. Returns something to stop it with.
 *
 * The watch is restarted whenever the screen comes back on: both iOS and
 * Android quietly stop delivering readings to a backgrounded page, and the
 * symptom is an app that looks fine and pins everything at the last place you
 * looked at it.
 */
export function watchFix(
  onSample: (sample: Sample) => void,
  onProblem: (problem: GeoProblem) => void,
): Watcher {
  if (typeof navigator === "undefined" || navigator.geolocation === undefined) {
    onProblem("unsupported");
    return { stop: (): void => undefined };
  }

  let id: number | null = null;

  const deliver = (position: GeolocationPosition): void => {
    onSample({
      lat: position.coords.latitude,
      lon: position.coords.longitude,
      accuracyM: position.coords.accuracy,
      altitudeM: position.coords.altitude,
      at: position.timestamp,
    });
  };

  const start = (): void => {
    if (id !== null) navigator.geolocation.clearWatch(id);
    id = navigator.geolocation.watchPosition(
      deliver,
      (error) => onProblem(problemOf(error)),
      OPTIONS,
    );
  };

  const onVisible = (): void => {
    if (document.visibilityState === "visible") start();
  };

  start();
  document.addEventListener("visibilitychange", onVisible);

  return {
    stop: (): void => {
      document.removeEventListener("visibilitychange", onVisible);
      if (id !== null) navigator.geolocation.clearWatch(id);
      id = null;
    },
  };
}
